import { format, fromUnixTime, parseISO, isAfter } from "date-fns";
import { SubscriptionStatus } from "src/apis/metricsApi";
import {
  EMPTY_VALUE,
  ReferredByOptionId,
  REFERRED_BY_OPTIONS,
} from "./constants";
import type {
  AssignedToOptionType,
  LeadUserType,
  MemberUserType,
  UpdateProfileData,
  UsersInfoType,
} from "./types";
import type { StaffRecordType } from "../People/types";
import type { V2Member } from "../../apis/userApi";
import { formatCurrency } from "../../utils/currencyUtils";

export function convertDateToUsDateFormat(data?: Date | null) {
  if (!data) return EMPTY_VALUE;
  return format(data, "MM/dd/yyyy");
}

export function convertDateStringOrTimestampToUsDateFormat(
  data?: string | number | null,
) {
  if (!data) return EMPTY_VALUE;
  if (typeof data === "number") {
    return format(fromUnixTime(data), "MM/dd/yyyy");
  }
  return format(parseISO(data.split("T")[0]), "MM/dd/yyyy");
}

export function convertIsoDateOrTimestampToUSDateAndTimeFormat(
  data: string | number,
) {
  let date: Date;
  if (typeof data === "number") {
    date = new Date(data * 1000);
  } else {
    date = parseISO(data);
  }
  return {
    date: format(date, "MM/dd/yyyy"),
    time: format(date, "hh:mm a"),
  };
}

export function convertDateStringOrTimestampToDate(
  data?: string | number | null,
) {
  if (!data) return undefined;
  if (typeof data === "number") {
    return fromUnixTime(data);
  }
  return parseISO(data.split("T")[0]);
}

export function getUserType(data?: string | null) {
  if (!data) {
    return EMPTY_VALUE;
  }
  return `${data.charAt(0).toUpperCase()}${data.slice(1)}`;
}

// RFC 5322 Official Standard Email Validation
export const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const PHONE_NUMBER_REGEX = /^\+?[1-9]\d{1,14}$/;

export function getAssignedToStaffOptions(
  data: StaffRecordType[],
): AssignedToOptionType[] {
  return [
    ...(data.map((staff) => ({
      value: staff.userId?.toString(),
      label: `${staff.firstName} ${staff.lastName} (${staff.membershipRole})`,
      userId: staff.userId,
      email: staff.email,
    })) || []),
    {
      value: "0",
      label: "Unassigned",
      email: "",
      userId: 0, // 0 is the "unassigned" value in DB
    },
  ];
}

export function getReferredByUserOptions(
  data: MemberUserType[] | StaffRecordType[],
) {
  return [
    ...data.map((member) => ({
      value: member.userId?.toString(),
      label: `${member.firstName} ${member.lastName}`,
      email: member.email || "",
    })),
    {
      value: "0", // 0 is the "unassigned" value in DB
      label: "Unassigned",
      email: "",
    },
  ];
}

function findByUserId(
  memberId: number,
  data: MemberUserType[] | StaffRecordType[],
) {
  const member = data.find((member) => member.userId === memberId);
  return member ? `${member?.firstName} ${member?.lastName}` : EMPTY_VALUE;
}

export function getReferredBySelectOption(
  referredById: string,
  referredByOther: string,
  referredByUserId: string,
  members: MemberUserType[],
  staff: StaffRecordType[],
) {
  if (referredById === ReferredByOptionId.Unassigned) return "Unassigned";
  if (referredById === ReferredByOptionId.Advertisement)
    return `Advertisement: ${referredByOther}`;
  if (referredById === ReferredByOptionId.Other)
    return `Other: ${referredByOther}`;

  if (referredById === ReferredByOptionId.ExistingMember)
    return `Existing Member: ${findByUserId(Number(referredByUserId), members)}`;
  if (referredById === ReferredByOptionId.StaffMember)
    return `Staff Member: ${findByUserId(Number(referredByUserId), staff)}`;

  return (
    REFERRED_BY_OPTIONS().find((option) => option.optionId === referredById)
      ?.label || EMPTY_VALUE
  );
}

export function getProfilePayload(
  data: UpdateProfileData[],
): Record<string, string | number | boolean | null> {
  return data.reduce((acc, item) => {
    acc[item.field] = item.value;
    return acc;
  }, {});
}

export function getParentAccountInfo(
  profileData?: V2Member,
  usersInfo?: UsersInfoType,
): MemberUserType | LeadUserType | StaffRecordType | null {
  if (!profileData || !usersInfo) return null;

  const users = [...usersInfo.members, ...usersInfo.leads, ...usersInfo.staff];
  const parentAccount = users.find(
    (user: MemberUserType | LeadUserType | StaffRecordType) =>
      user.userId === profileData.parentUserId,
  );
  return parentAccount || null;
}

export function isPaymentMethodExpired(expMonth: string, expYear: string) {
  if (!expMonth || !expYear) return false;
  const today = new Date();
  const expiration = new Date(parseInt(expYear), parseInt(expMonth), 0);
  return today > expiration;
}

export function buildURLWithEnvironment(subdomain: string, path: string) {
  const environment = process.env.NODE_ENV;
  const protocol = environment === "local" ? "http://" : "https://";
  const getHost = (env) => {
    switch (env) {
      case "development":
        return "pushpressdev.com";
      case "stage":
        return "pushpressstage.com";
      case "production":
        return "pushpress.com";
      case "local":
      default:
        return "pushpressdev.com";
    }
  };
  const host = getHost(environment);

  return `${protocol}${subdomain}.${host}${path}`;
}

type UserProfileTemplate = "user-document-requested" | "email-profile-sent";

type BuildEmailParams = {
  company: string;
  template: UserProfileTemplate;
  toAddress: string;
  userUuid: string;
  clientId: number;
  clientUuid: string;
  userId: number;
  values: {
    client: {
      company: string;
      address?: string;
      email: string;
      phone?: string;
      url?: string;
    };
    cta_link: string;
    cta_text?: string;
    email_reason?: string;
  };
};

export function buildEmailParamsFromTemplate({
  clientId,
  clientUuid,
  company,
  template,
  toAddress,
  userId,
  userUuid,
  values,
}: BuildEmailParams) {
  const params = {
    action: template,
    clientId,
    clientUuid,
    emailType: template,
    subject: `[Action Needed] ${company} has documents that need your signature!`,
    senderName: company,
    template,
    toAddress,
    userId,
    userUuid,
    values,
  };
  switch (template) {
    case "user-document-requested":
      params.subject = `[Action Needed] ${company} has documents that need your signature!`;
      break;
    case "email-profile-sent":
      params.subject = `[Action Needed] Setup Your Profile at ${company}`;
      break;
  }
  return params;
}

type ProfileSubscriptionStatus = SubscriptionStatus | "pendpause";

export function getSubscriptionStatus({
  status,
  pauseDate,
}: {
  status: SubscriptionStatus;
  pauseDate: string;
}): ProfileSubscriptionStatus {
  const pause = parseISO(pauseDate);
  const today = new Date();
  if (isAfter(pause, today)) {
    return "pendpause";
  }
  return status;
}

export function getPlansDetails({ amount, discountAmount, currencyIso }) {
  if (discountAmount) {
    return `${formatCurrency(amount - discountAmount, currencyIso)} (${formatCurrency(amount, currencyIso)} - ${formatCurrency(discountAmount, currencyIso)})`;
  }
  return formatCurrency(amount, currencyIso);
}
